/**
 * Dashboard Principal (Rediseñado)
 * Pantalla de inicio premium según diseño
 */

import React from 'react';
import styled from 'styled-components';
import { Spin, DatePicker } from 'antd';
import { useDashboard } from '../../hooks/useDashborard';
import { getCurrentUser } from '../../services/auth';
import dayjs from 'dayjs';

// Sub-components
import DashboardHeader from './components/DashboardHeader';
import KPICards from './components/KPICards';
import SpecialtiesChart from './components/SpecialtiesChart';
import RefreshCard from './components/RefreshCard';
import CoverageMap from './components/CoverageMap';
import PatientStats from './components/PatientStats';

/* ================== Styles ================== */

const PageContainer = styled.div`
  background-color: #f0f7ff;
  min-height: 100vh;
  padding: 20px;
  font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 24px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

/* ================== Dashboard ================== */

const Dashboard = () => {
  const [selectedMonth, setSelectedMonth] = React.useState(dayjs());
  const user = getCurrentUser();

  // Calculate range if month is selected, or default to current month
  const params = React.useMemo(() => {
    const date = selectedMonth || dayjs();
    return {
      startDate: date.startOf('month').format('YYYY-MM-DD'),
      endDate: date.endOf('month').format('YYYY-MM-DD'),
    };
  }, [selectedMonth]);

  const { data, loading, refresh } = useDashboard(params, 5);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80, background: '#f0f7ff', minHeight: '100vh' }}>
        <Spin size="large" tip="Cargando Dashboard..." />
      </div>
    );
  }

  // Format large numbers
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num.toString();
  };

  return (
    <PageContainer>
      {/* Header / Breadcrumb */}
      <DashboardHeader
        userName={user?.name || 'Admin'}
        profileImage={user?.profileImage}
      >
        <DatePicker
          picker="month"
          value={selectedMonth}
          onChange={(val) => setSelectedMonth(val)}
          placeholder="Mes en curso"
          allowClear={true}
          style={{ borderRadius: '8px' }}
        />
      </DashboardHeader>

      {/* KPI Cards */}
      <KPICards data={data} formatNumber={formatNumber} />

      {/* Main Grid */}
      <DashboardGrid>
        {/* Especialidades Médicas */}
        <SpecialtiesChart data={data} />

        {/* Global Stats / Refresh */}
        <RefreshCard onRefresh={refresh} />

        {/* Cobertura por Ciudad */}
        <CoverageMap data={data} />

        {/* Pacientes por Ubicación y Género */}
        <PatientStats data={data} />
      </DashboardGrid>
    </PageContainer>
  );
};

export default Dashboard;
